import React from 'react';
import { buildSuggestions, GraphMember, Relationship, Side } from '@/contexts/FamilyGraphContext';
import { Sparkles, Plus } from 'lucide-react';

interface Props {
  members: GraphMember[];
  onSelect: (input: { relationship: Relationship; side: Side; prefillName?: string }) => void;
  className?: string;
}

const colorForRel = (rel: Relationship) => {
  switch (rel) {
    case 'mother':
    case 'father':
      return 'from-pink-500 to-fuchsia-500';
    case 'grandparent':
    case 'great-grandparent':
      return 'from-purple-500 to-indigo-500';
    case 'aunt-uncle':
      return 'from-amber-500 to-orange-500';
    case 'cousin':
    case 'cousin-child':
      return 'from-emerald-500 to-teal-500';
    case 'sibling':
      return 'from-sky-500 to-blue-500';
    default:
      return 'from-fuchsia-500 to-pink-500';
  }
};

const SmartSuggestions: React.FC<Props> = ({ members, onSelect, className = '' }) => {
  const suggestions = React.useMemo(() => buildSuggestions(members).slice(0, 6), [members]);

  if (suggestions.length === 0) return null;

  return (
    <div
      className={`rounded-2xl bg-gradient-to-br from-white to-fuchsia-50 border-2 border-fuchsia-100 p-5 ${className}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-5 h-5 text-fuchsia-600" />
        <h3 className="font-serif font-bold text-lg text-slate-900">
          Smart suggestions for your tree
        </h3>
      </div>
      <p className="text-xs text-slate-600 mb-3">
        Tap a card to grow your family graph in one tap.
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
        {suggestions.map((s) => (
          <button
            key={s.id}
            onClick={() =>
              onSelect({
                relationship: s.relationship,
                side: s.side,
              })
            }
            className={`group text-left rounded-xl p-3 bg-gradient-to-br ${colorForRel(
              s.relationship
            )} text-white shadow-sm hover:shadow-md hover:scale-[1.02] transition-all`}
          >
            <div className="flex items-start gap-2">
              <div className="w-8 h-8 rounded-full bg-white/25 flex items-center justify-center shrink-0">
                <Plus className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm leading-tight">{s.label}</div>
                <div className="text-[11px] opacity-90 mt-0.5">
                  {s.side === 'mother'
                    ? "Mom's side"
                    : s.side === 'father'
                    ? "Dad's side"
                    : 'Shared'}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default SmartSuggestions;
